import { FileTextIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Drawer } from "vaul";

import { Card } from "@/components/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

import { VttDialog, parseVtt, parseVttTime } from "../utils/parse-vtt";

type SrtDialogProps = {
  dialog: VttDialog;
};

export function DialogItem({ dialog }: SrtDialogProps) {
  const [start] = dialog.time.split(" --> ");
  const formattedStart = parseVttTime(start);
  return (
    <div className="mb-3 flex gap-2">
      <span className="text-muted-foreground">{formattedStart}</span>
      <p className="text-foreground">{dialog.text}</p>
    </div>
  );
}

type ViewTranscriptDrawerProps = {
  sessionName: string;
  transcript: string;
};

export function ViewTranscriptDrawer({
  sessionName,
  transcript,
}: ViewTranscriptDrawerProps) {
  const [open, setOpen] = useState(false);

  const parsedTranscript = parseVtt(transcript);

  const handleSrtDownload = () => {
    const blob = new Blob([transcript], { type: "text/vtt" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${sessionName}_transcript.vtt`;
    a.click();
    setOpen(false);

    toast.success("Transcript downloaded");
  };

  return (
    <Drawer.Root direction="right" open={open} onOpenChange={setOpen}>
      <Drawer.Trigger asChild>
        <Button variant="outline">
          <FileTextIcon className="mr-2 size-4" aria-hidden={true} /> View
          Transcript
        </Button>
      </Drawer.Trigger>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-foreground/70 dark:bg-background/40" />
        <Drawer.Content className="fixed right-0 top-0 z-10 flex h-full w-[542px] max-w-[100%] p-4 outline-none">
          <Card
            className="h-full max-h-[100vh] min-h-full w-full"
            subContainerClassName="min-h-full max-h-full"
          >
            <Card.Header
              title="Session Transcript"
              className="flex flex-row items-center justify-between"
            >
              <Drawer.Close asChild>
                <Button variant="ghost" size="icon">
                  <XIcon className="size-4" aria-hidden={true} />
                </Button>
              </Drawer.Close>
            </Card.Header>
            <Card.Content className="flex min-h-full flex-1 flex-col justify-between gap-4">
              <ScrollArea
                className="flex h-full min-h-full flex-1 flex-col gap-4 text-sm text-muted-foreground"
                type="always"
              >
                {parsedTranscript.map((item) => (
                  <DialogItem key={item.time} dialog={item} />
                ))}
              </ScrollArea>
              <div className="flex flex-row items-center justify-end">
                <Button
                  variant="default"
                  className="justify-end self-end"
                  onClick={handleSrtDownload}
                >
                  Download
                </Button>
              </div>
            </Card.Content>
          </Card>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
